'use client';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type UserRole = 'executive' | 'administrator' | 'lra_staff' | 'taxpayer' | 'lla_staff' | 'field_enumerator' | 'county_admin';
export type AuthorityWorkspace = 'lra' | 'lla';

export interface AuthUser {
  email: string;
  name: string;
  initials: string;
  title: string;
  role: UserRole;
  authority?: AuthorityWorkspace;
  jurisdiction?: string; // county/district for field_enumerator and county_admin
}

interface AuthContextValue {
  user: AuthUser | null;
  setUser: (user: AuthUser | null) => void;
  logout: () => void;
  hasAccess: (requiredRoles: UserRole[]) => boolean;
  activeWorkspace: AuthorityWorkspace;
  setActiveWorkspace: (ws: AuthorityWorkspace) => void;
  canSwitchWorkspace: boolean;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  setUser: () => {},
  logout: () => {},
  hasAccess: () => false,
  activeWorkspace: 'lra',
  setActiveWorkspace: () => {},
  canSwitchWorkspace: false,
});

// Roles that can access LLA workspace
const LLA_WORKSPACE_ROLES: UserRole[] = ['executive', 'administrator', 'lla_staff', 'field_enumerator', 'county_admin'];
// Roles that can access LRA workspace
const LRA_WORKSPACE_ROLES: UserRole[] = ['executive', 'administrator', 'lra_staff'];
// Roles that can switch between workspaces
const DUAL_WORKSPACE_ROLES: UserRole[] = ['executive', 'administrator'];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<AuthUser | null>(null);
  const [activeWorkspace, setActiveWorkspaceState] = useState<AuthorityWorkspace>('lra');

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('lra_user');
      if (stored) {
        const parsed = JSON.parse(stored) as AuthUser;
        setUserState(parsed);
        // Set default workspace based on role
        if (parsed.role === 'lla_staff' || parsed.role === 'field_enumerator' || parsed.role === 'county_admin') {
          setActiveWorkspaceState('lla');
        }
      }
      const storedWs = sessionStorage.getItem('active_workspace') as AuthorityWorkspace | null;
      if (storedWs) setActiveWorkspaceState(storedWs);
    } catch {
      // ignore
    }
  }, []);

  const setUser = (u: AuthUser | null) => {
    setUserState(u);
    try {
      if (u) {
        sessionStorage.setItem('lra_user', JSON.stringify(u));
        // Auto-set workspace based on role
        if (u.role === 'lla_staff' || u.role === 'field_enumerator' || u.role === 'county_admin') {
          setActiveWorkspaceState('lla');
          sessionStorage.setItem('active_workspace', 'lla');
        }
      } else {
        sessionStorage.removeItem('lra_user');
        sessionStorage.removeItem('active_workspace');
      }
    } catch {
      // ignore
    }
  };

  const setActiveWorkspace = (ws: AuthorityWorkspace) => {
    // LLA-only roles cannot switch to LRA workspace
    if (user && ['lla_staff', 'field_enumerator', 'county_admin'].includes(user.role) && ws === 'lra') {
      return;
    }
    setActiveWorkspaceState(ws);
    try {
      sessionStorage.setItem('active_workspace', ws);
    } catch {
      // ignore
    }
  };

  const logout = () => {
    setUser(null);
    window.location.href = '/sign-up-login-screen';
  };

  const hasAccess = (requiredRoles: UserRole[]) => {
    if (!user) return false;
    return requiredRoles.includes(user.role);
  };

  const canSwitchWorkspace = user ? DUAL_WORKSPACE_ROLES.includes(user.role) : false;

  return (
    <AuthContext.Provider value={{ user, setUser, logout, hasAccess, activeWorkspace, setActiveWorkspace, canSwitchWorkspace }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Role Tier Definitions:
 * LRA Workspace:
 * - executive:        Read-only access to ALL dashboards and analytics
 * - administrator:    Full access to Stage 4–8 config and operations
 * - lra_staff:        Stage 6–8 billing and payment matching operations
 * - taxpayer:         Self-service portal only
 *
 * LLA Workspace:
 * - lla_staff:        Full spatial + legal write; read parcel tax status only
 * - field_enumerator: Submit parcels (own jurisdiction only); offline-first
 * - county_admin:     QA approve, manage records (own county only)
 */
export const ROLE_ACCESS: Record<string, UserRole[]> = {
  // Dashboard
  '/': ['executive', 'administrator', 'lra_staff'],
  '/taxpayer-self-service-portal': ['executive', 'administrator', 'lra_staff', 'taxpayer'],

  // Administration (S4–S8)
  '/taxpayer-registry': ['executive', 'administrator', 'lra_staff'],
  '/compliance-engine': ['executive', 'administrator', 'lra_staff'],
  '/tax-filings': ['executive', 'administrator', 'lra_staff'],
  '/collections': ['executive', 'administrator', 'lra_staff'],
  '/property-payments-matching': ['executive', 'administrator', 'lra_staff'],
  '/valuation-config': ['executive', 'administrator'],
  '/tax-assessment-admin': ['executive', 'administrator'],

  // Stage 1 — Property Registration
  '/property-registration-admin': ['executive', 'administrator', 'lra_staff'],

  // Stage 2 — Parcel Delineation
  '/parcel-delineation': ['executive', 'administrator', 'lra_staff'],

  // Stage 3 — Ownership Verification
  '/ownership-verification': ['executive', 'administrator', 'lra_staff'],

  // Stage 6 — Billing Dashboard
  '/billing-dashboard': ['executive', 'administrator', 'lra_staff'],

  // Analytics
  '/revenue-analytics': ['executive', 'administrator'],
  '/county-payment-performance': ['executive', 'administrator'],
  '/customs-border': ['executive', 'administrator'],

  // GIS & Mapping
  '/gis-property-hub': ['executive', 'administrator', 'lra_staff', 'lla_staff', 'county_admin'],

  // Methodology & Presentation
  '/property-tax-cycle': ['executive', 'administrator', 'lra_staff'],
  '/system-methodology': ['executive', 'administrator', 'lra_staff'],
  '/nine-circle-methodology': ['executive', 'administrator', 'lra_staff'],
  '/executive-briefing': ['executive', 'administrator'],
  '/problem-solution': ['executive', 'administrator'],
  '/resources': ['executive', 'administrator', 'lra_staff'],

  // System
  '/enterprise-admin': ['executive', 'administrator'],
  '/county-offices': ['executive', 'administrator', 'lra_staff'],

  // ── LLA CADASTRAL MODULES ──
  '/lla/parcel-registry': ['executive', 'administrator', 'lla_staff', 'county_admin'],
  '/lla/cadastral-map': ['executive', 'administrator', 'lla_staff', 'lra_staff', 'county_admin', 'field_enumerator'],
  '/lla/ownership-tenure': ['executive', 'administrator', 'lla_staff'],
  '/lla/field-enumerator': ['executive', 'administrator', 'lla_staff', 'field_enumerator', 'county_admin'],
  '/lla/county-qa': ['executive', 'administrator', 'lla_staff', 'county_admin'],
  '/lla/objections-appeals': ['executive', 'administrator', 'lla_staff', 'lra_staff'],
  '/lla/public-lookup': ['executive', 'administrator', 'lla_staff', 'lra_staff', 'taxpayer', 'field_enumerator', 'county_admin'],
  '/lla/national-audit': ['executive', 'administrator'],
};

export const LLA_WORKSPACE_ROLES_LIST = LLA_WORKSPACE_ROLES;
export const LRA_WORKSPACE_ROLES_LIST = LRA_WORKSPACE_ROLES;
